import { useLayoutEffect, useRef } from "react";
import { isEditableTarget } from "../lib/isEditableTarget";
import {
  beforeNavigation,
  isRouteLocation,
  routes,
  useCurrentLocation,
  useHistoryEntryKey,
} from "../lib/urls";

const scrollPositions = new Map<string, number>();

function pageLocation(location: string) {
  const url = new URL(location, window.location.origin);
  for (const key of [...url.searchParams.keys()]) {
    if (key === "modal" || key.startsWith("modal[")) {
      url.searchParams.delete(key);
    }
  }
  if (isRouteLocation(routes.monitoring.workflows, location)) {
    // Workflow monitoring drill-ins keep their modal state in page-level parameters.
    for (const key of [
      "panel",
      "space",
      "pspace",
      "auth",
      "view",
      "search",
      "sort",
    ]) {
      url.searchParams.delete(key);
    }
  }
  url.searchParams.sort();
  return `${url.pathname}${url.search}${url.hash}`;
}

export type ScrollRestorationOptions = {
  active?: boolean;
  ready: boolean;
  resetOnNavigate?: boolean;
};

export function useScrollRestoration<T extends HTMLElement>({
  active = true,
  ready,
  resetOnNavigate = true,
}: ScrollRestorationOptions) {
  const scrollRef = useRef<T | null>(null);
  const previousRef = useRef<{ page: string; top: number } | null>(null);
  const page = pageLocation(useCurrentLocation());
  const entryKey = useHistoryEntryKey();

  useLayoutEffect(() => {
    const scroll = scrollRef.current;
    if (!scroll || !active) return;

    const key = `${entryKey}:${page}`;
    const previous = previousRef.current;
    const target =
      scrollPositions.get(key) ??
      (previous && (previous.page === page || !resetOnNavigate)
        ? previous.top
        : 0);
    let pending = !ready;
    const save = () => {
      const top = pending ? target : scroll.scrollTop;
      scrollPositions.set(key, top);
      previousRef.current = { page, top };
    };
    const onScroll = () => {
      if (!pending) save();
    };
    const onInteraction = (event: Event) => {
      if (event.defaultPrevented) return;
      if (event.type === "pointerdown" && event.target !== scroll) return;
      if (event.type === "keydown") {
        const keyboardEvent = event as KeyboardEvent;
        if (
          ![
            "ArrowUp",
            "ArrowDown",
            "PageUp",
            "PageDown",
            "Home",
            "End",
            " ",
          ].includes(keyboardEvent.key) ||
          isEditableTarget(keyboardEvent) ||
          (event.target instanceof Element &&
            event.target.closest("button, select"))
        )
          return;
      }
      pending = false;
      save();
    };
    const interactions = ["wheel", "touchmove", "pointerdown", "keydown"];
    for (const event of interactions) {
      scroll.addEventListener(event, onInteraction, { passive: true });
    }
    scroll.addEventListener("scroll", onScroll, { passive: true });
    const unsubscribe = beforeNavigation(save);

    // Once the content is loaded, accept the clamped position if the list has become shorter.
    if (ready) scroll.scrollTop = target;
    save();

    return () => {
      unsubscribe();
      scroll.removeEventListener("scroll", onScroll);
      for (const event of interactions) {
        scroll.removeEventListener(event, onInteraction);
      }
    };
  }, [active, entryKey, page, ready, resetOnNavigate]);

  return scrollRef;
}
